const getAssessmentState = state => state.assessment

export const selectors = {
  getQuestions: state =>
    getAssessmentState(state)?.currentAssessment?.questions || [],
  getTotalQuestions: state =>
    getAssessmentState(state)?.currentAssessment?.questions?.total_questions ||
    0,
  getLoading: state => getAssessmentState(state)?.loading || false,
  getError: state => getAssessmentState(state)?.error || null,
  getResponses: state =>
    getAssessmentState(state)?.currentAssessment?.responses || {},
  getCurrentAssessmentId: state =>
    getAssessmentState(state)?.currentAssessment?.assessment_id || '',
  getCurrentAssessmentSetId: state =>
    getAssessmentState(state)?.currentAssessment?.assessment_set_id || '',
  getCurrentAssessment: state =>
    getAssessmentState(state)?.currentAssessment || {},
  getCurrentBehaviorId: state => {
    const questions =
      getAssessmentState(state)?.currentAssessment?.questions || {}
    const responses =
      getAssessmentState(state)?.currentAssessment?.responses || {}

    // Handle behavior assessment questions
    if (questions.behaviors && Array.isArray(questions.behaviors)) {
      const currentBehavior = questions.behaviors.find(
        behavior => !responses[behavior.id]
      )
      return currentBehavior
        ? currentBehavior.id
        : questions.behaviors[0]?.id || ''
    }

    // Handle communication assessment questions
    if (questions.communications && Array.isArray(questions.communications)) {
      return '' // Communication questions don't use behavior IDs
    }

    return ''
  },
  getActiveAssessments: state =>
    getAssessmentState(state)?.activeAssessments?.data || [],
  getActiveAssessmentsLoading: state =>
    getAssessmentState(state)?.activeAssessments?.loading || false,
  getActiveAssessmentsError: state =>
    getAssessmentState(state)?.activeAssessments?.error || null,
  getCompletedAssessments: state =>
    getAssessmentState(state)?.completedAssessments?.data || [],
  getCompletedAssessmentsLoading: state =>
    getAssessmentState(state)?.completedAssessments?.loading || false,
  getCompletedAssessmentsError: state =>
    getAssessmentState(state)?.completedAssessments?.error || null,
  getActiveAssessmentsFilters: state =>
    getAssessmentState(state)?.activeAssessmentsFilters || {
      duration: '',
      assessmentType: '',
      search: '',
    },
  getCompletedAssessmentsFilters: state =>
    getAssessmentState(state)?.completedAssessmentsFilters || {
      timeFilter: '',
      assessmentType: '',
      search: '',
    },
  // Reports selectors
  getReports: state => getAssessmentState(state)?.reports?.data || [],
  getReportsLoading: state =>
    getAssessmentState(state)?.reports?.loading || false,
  getReportsError: state => getAssessmentState(state)?.reports?.error || null,
  // Practice access selectors
  getPracticeAccess: state =>
    getAssessmentState(state)?.practiceAccess?.hasAccess || false,
  getPracticeAccessLoading: state =>
    getAssessmentState(state)?.practiceAccess?.loading || false,
  getPracticeAccessError: state =>
    getAssessmentState(state)?.practiceAccess?.error || null,

  // UPDATED: Practice history selectors with better type filtering
  getPracticeHistory: state =>
    getAssessmentState(state)?.practiceAssessments?.history || [],
  getPracticeHistoryLoading: state =>
    getAssessmentState(state)?.practiceAssessments?.loading || false,
  getPracticeHistoryError: state =>
    getAssessmentState(state)?.practiceAssessments?.error || null,
  getPracticeHistoryPagination: state =>
    getAssessmentState(state)?.practiceAssessments?.pagination || null,

  getDropoutAssessments: state => {
    const result = getAssessmentState(state)?.dropoutAssessments?.data || []
    console.log(
      '🔍 Selector: getDropoutAssessments called, state:',
      getAssessmentState(state)?.dropoutAssessments
    )
    console.log('🔍 Selector: getDropoutAssessments returning:', result)
    return result
  },
  getDropoutAssessmentsLoading: state =>
    getAssessmentState(state)?.dropoutAssessments?.loading || false,
  getDropoutAssessmentsError: state =>
    getAssessmentState(state)?.dropoutAssessments?.error || null,

  getDropoutAssessmentsFilters: state =>
    getAssessmentState(state)?.dropoutAssessmentsFilters || {
      timeFilter: '',
      assessmentType: '',
      search: '',
    },

  // Direct Assessment selectors
  getDirectAssessment: state =>
    getAssessmentState(state)?.directAssessment?.assessment || null,
  getDirectAssessmentLoading: state =>
    getAssessmentState(state)?.directAssessment?.loading || false,
  getDirectAssessmentError: state =>
    getAssessmentState(state)?.directAssessment?.error || null,

  // Communication practice history selectors
  getCommunicationPracticeHistory: state => {
    const allHistory =
      getAssessmentState(state)?.practiceAssessments?.history || []
    return allHistory.filter(assessment => {
      const type = (
        assessment.type ||
        assessment.assessmentType ||
        ''
      ).toLowerCase()
      return type === 'communication'
    })
  },

  getAptitudePracticeHistory: state => {
    const allHistory =
      getAssessmentState(state)?.practiceAssessments?.history || []
    return allHistory.filter(assessment => {
      const type = (
        assessment.type ||
        assessment.assessmentType ||
        ''
      ).toLowerCase()
      return type === 'aptitude'
    })
  },

  // Reports selectors
  getReports: state => getAssessmentState(state)?.reports?.data || [],
  getReportsLoading: state =>
    getAssessmentState(state)?.reports?.loading || false,
  getReportsError: state => getAssessmentState(state)?.reports?.error || null,

  // Retake eligibility selectors
  getRetakeEligibility: state =>
    getAssessmentState(state)?.retakeEligibility?.data || null,
  getRetakeEligibilityLoading: state =>
    getAssessmentState(state)?.retakeEligibility?.loading || false,
  getRetakeEligibilityError: state =>
    getAssessmentState(state)?.retakeEligibility?.error || null,

  // FIXED: Timer selectors
  getTimeSpentPerQuestion: state =>
    getAssessmentState(state)?.timeSpentPerQuestion || {},
  getActiveQuestionTimer: state =>
    getAssessmentState(state)?.activeQuestionTimer || null,
  getQuestionTime: (state, questionId) =>
    getAssessmentState(state)?.timeSpentPerQuestion?.[questionId] || 0,
  // FIXED: Student data selectors
  getStudentData: state =>
    state.studentData?.studentDetails || state.auth?.user || null,
  getStudentName: state => {
    const studentData =
      state.studentData?.studentDetails || state.auth?.user || null
    return (
      studentData?.full_name ||
      studentData?.fullName ||
      studentData?.firstName ||
      studentData?.name ||
      'Unknown Student'
    )
  },
  // NEW: Solutions selectors
  getSolutions: state => getAssessmentState(state)?.solutions?.data || null,
  getSolutionsLoading: state =>
    getAssessmentState(state)?.solutions?.loading || false,
  getSolutionsError: state =>
    getAssessmentState(state)?.solutions?.error || null,
  // NEW: Aptitude score selectors
  getAptitudeScore: state =>
    getAssessmentState(state)?.aptitudeScore?.data || null,
  getAptitudeScoreLoading: state =>
    getAssessmentState(state)?.aptitudeScore?.loading || false,
  getAptitudeScoreError: state =>
    getAssessmentState(state)?.aptitudeScore?.error || null,

  // Enhanced practice access selectors
  getPracticeAccessState: state =>
    getAssessmentState(state)?.practiceAccess || {},
  getCommunicationPracticeAccess: state =>
    getAssessmentState(state)?.practiceAccess?.Communication || {
      hasAccess: false,
    },
  getAptitudePracticeAccess: state =>
    getAssessmentState(state)?.practiceAccess?.Aptitude || { hasAccess: false },
  getOverallPracticeAccess: state => {
    const practiceAccess = getAssessmentState(state)?.practiceAccess || {}
    return practiceAccess.hasAccess || false
  },
  // New diagnosis selectors
  getDiagnosisStatus: state =>
    getAssessmentState(state)?.diagnosisStatus?.data || {
      communication: { completed: 0, required: 2 },
      aptitude: { completed: 0, required: 2 }
    },
  getDiagnosisStatusLoading: state =>
    getAssessmentState(state)?.diagnosisStatus?.loading || false,
  getDiagnosisStatusError: state =>
    getAssessmentState(state)?.diagnosisStatus?.error || null,

  // Add to selectors
  getStudentCorporateStatus: state => 
    getAssessmentState(state)?.studentCorporateStatus?.isCorporate || false,
}

// Updated selectors that look at the correct state slice
export const selectAllAssessments = state => {
  // FIXED: Only return practice assessments, not normal assessments
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments
}

// FIXED: Update these selectors to only count practice assessments
export const selectUpdatedCommunicationAssessmentCount = state => {
  // Only count practice assessments
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'communication'
  ).length
}

export const selectUpdatedAptitudeAssessmentCount = state => {
  // Only count practice assessments
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'aptitude'
  ).length
}

export const selectUpdatedTechnicalAssessmentCount = state => {
  // Only count practice assessments
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'technical'
  ).length
}

export const selectUpdatedTotalPracticeHours = state => {
  // Only calculate hours from practice assessments
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments
    .reduce((sum, assessment) => {
      const duration = assessment.duration || 0
      return sum + duration / 60
    }, 0)
    .toFixed(2)
}

export const selectActivityData = state => state.assessment?.activityData || []
export const selectActivityLoading = state =>
  state.assessment?.activityLoading || false
export const selectActivityError = state =>
  state.assessment?.activityError || null
export const selectActivityDateRange = state =>
  state.assessment?.activityDateRange || { startDate: null, endDate: null }

// Practice Scores selectors with type filtering
export const selectPracticeScores = state => {
  const scores = state.assessment?.practiceScores?.data || []
  return scores
}

// ENHANCED: Type-specific practice scores selectors with better filtering
export const selectCommunicationPracticeScores = state => {
  const scores = state.assessment?.practiceScores?.data || []
  return scores.filter(score => {
    const type = (score.type || score.assessmentType || '').toLowerCase()
    return type === 'communication'
  })
}

export const selectAptitudePracticeScores = state => {
  const scores = state.assessment?.practiceScores?.data || []
  return scores.filter(score => {
    const type = (score.type || score.assessmentType || '').toLowerCase()
    return type === 'aptitude'
  })
}

export const selectPracticeScoresLoading = state => {
  const loading = state.assessment?.practiceScores?.loading || false
  return loading
}

export const selectPracticeScoresError = state => {
  const error = state.assessment?.practiceScores?.error || null
  return error
}

// Completed Practice Assessments selectors
export const selectCompletedPracticeAssessments = state =>
  state?.assessment?.completedPracticeAssessments?.data ?? []

export const selectCompletedPracticeAssessmentsLoading = state =>
  state?.assessment?.completedPracticeAssessments?.loading ?? false

export const selectCompletedPracticeAssessmentsError = state =>
  state?.assessment?.completedPracticeAssessments?.error ?? null

export const selectCompletedPracticeAssessmentsPagination = state =>
  state?.assessment?.completedPracticeAssessments?.pagination ?? null

// Practice hours calculation selectors
export const selectTotalPracticeHours = state => {
  const completedAssessments =
    state?.assessment?.completedPracticeAssessments?.data ?? []

  const totalHours = completedAssessments.reduce((total, assessment) => {
    return total + (assessment.duration || 0)
  }, 0)

  return totalHours
}

// Helper selector to get formatted total hours
export const selectFormattedTotalPracticeHours = state => {
  const totalHours = selectTotalPracticeHours(state)

  if (totalHours < 1) {
    const minutes = Math.round(totalHours * 60)
    return minutes
  } else {
    const hours = Math.floor(totalHours)
    const minutes = Math.round((totalHours - hours) * 60)
    return hours + (minutes > 0 ? minutes / 60 : 0)
  }
}

// Helper selector to get communication assessment count
export const selectCommunicationAssessmentCount = state => {
  const pagination = selectCompletedPracticeAssessmentsPagination(state)
  const completedAssessments = selectCompletedPracticeAssessments(state)
  return pagination?.totalCount || completedAssessments?.length || 0
}

export const selectAptitudeAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  // Filter for aptitude assessments
  const aptitudeAssessments = completedAssessments.filter(assessment => {
    const type = (
      assessment.type ||
      assessment.assessmentType ||
      ''
    ).toLowerCase()
    return type === 'aptitude'
  })
  return aptitudeAssessments.length
}

export const selectTechnicalAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  // Filter for technical assessments
  const technicalAssessments = completedAssessments.filter(assessment => {
    const type = (
      assessment.type ||
      assessment.assessmentType ||
      ''
    ).toLowerCase()
    return type === 'technical'
  })
  return technicalAssessments.length
}

export const selectAptitudePracticeScores2 = state => {
  const scores = state.assessment?.aptitudePracticeScores?.data || []
  return scores
}

export const selectAptitudePracticeScoresLoading = state => {
  const loading = state.assessment?.aptitudePracticeScores?.loading || false
  return loading
}

export const selectAptitudePracticeScoresError = state => {
  const error = state.assessment?.aptitudePracticeScores?.error || null
  return error
}

export const selectAptitudeDayZero = state => {
  const dayZero = state.assessment?.aptitudePracticeScores?.dayZero || null
  return dayZero
}

export const selectCommunicationDayZero = state => {
  const dayZero = state.assessment?.practiceScores?.dayZero || null
  return dayZero
}

// NEW: Practice-only assessment count selectors
export const selectPracticeOnlyCommunicationCount = state => {
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'communication'
  ).length
}

export const selectPracticeOnlyAptitudeCount = state => {
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'aptitude'
  ).length
}

export const selectPracticeOnlyTechnicalCount = state => {
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment =>
      (assessment.type || assessment.assessmentType || '').toLowerCase() ===
      'technical'
  ).length
}

export const selectPracticeOnlyTotalHours = state => {
  const practiceAssessments =
    state.assessment?.completedPracticeAssessments?.data || []
  return practiceAssessments
    .reduce((sum, assessment) => {
      const duration = assessment.duration || 0
      return sum + duration / 60
    }, 0)
    .toFixed(2)
}

export default selectors

// Add this at the top if createSelector is not available
const createMemoizedSelector = selector => {
  let lastArgs = []
  let lastResult

  return state => {
    const currentArgs = [
      getAssessmentState(state)?.practiceAssessments?.history || [],
    ]

    // Simple shallow comparison
    if (
      currentArgs.length !== lastArgs.length ||
      currentArgs.some((arg, index) => arg !== lastArgs[index])
    ) {
      lastArgs = currentArgs
      lastResult = selector(...currentArgs)
    }

    return lastResult
  }
}

// Then replace the selectors with:
export const getCommunicationPracticeHistory = createMemoizedSelector(
  allHistory => {
    return allHistory.filter(assessment => {
      const type = (
        assessment.type ||
        assessment.assessmentType ||
        ''
      ).toLowerCase()
      return type === 'communication'
    })
  }
)

export const getAptitudePracticeHistory = createMemoizedSelector(allHistory => {
  return allHistory.filter(assessment => {
    const type = (
      assessment.type ||
      assessment.assessmentType ||
      ''
    ).toLowerCase()
    return type === 'aptitude'
  })
})
